/**
 * THE BOOT BLOB, CLIENT SIDE (launch-marketing-pages design M13, th-jki5kv.5).
 *
 * `homes#show` renders `PublicBoot.payload` into one
 * `<script type="application/json" id="th-public-boot">` on every SPA path, so
 * the runtime switches the SPA needs before its first paint arrive with the
 * page instead of over a request. This module parses that element once and
 * memoizes the answer.
 *
 * It is a leaf: it imports nothing from the app, so the public boot graph and
 * any store can read it without a cycle.
 *
 * It never throws. With no element (a test, a render outside `homes#show`) or
 * JSON that does not parse, it answers web push off, no VAPID key and an empty
 * claims map. A claim the map does not name reads as unknown, and a section
 * that claims it (plain or negated) stays hidden.
 */

export const PUBLIC_BOOT_ELEMENT_ID = "th-public-boot";

const DEFAULTS = Object.freeze({
  web_push_enabled: false,
  vapid_public_key: null,
  claims: Object.freeze({}),
});

let memo = null;

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function parse() {
  const element = typeof document === "undefined" ? null : document.getElementById(PUBLIC_BOOT_ELEMENT_ID);
  if (!element) return DEFAULTS;

  let raw;
  try {
    raw = JSON.parse(element.textContent);
  } catch {
    return DEFAULTS;
  }
  if (!isPlainObject(raw)) return DEFAULTS;

  return Object.freeze({
    web_push_enabled: raw.web_push_enabled === true,
    vapid_public_key: typeof raw.vapid_public_key === "string" && raw.vapid_public_key ? raw.vapid_public_key : null,
    claims: isPlainObject(raw.claims) ? Object.freeze({ ...raw.claims }) : DEFAULTS.claims,
  });
}

// The parsed blob: `{ web_push_enabled, vapid_public_key, claims }`, read from
// the page once.
export function readPublicBoot() {
  if (!memo) memo = parse();
  return memo;
}

export function __resetPublicBootForTests() {
  memo = null;
}
